import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import {
  validateField,
  emailValidator,
  passwordValidator,
  ValidationRule,
} from '@/utils/validators';

export function RegisterPage() {
  const { register, isRegistering } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = validateField(formData.name, {
      required: true,
      minLength: 2,
    } as ValidationRule);
    if (nameError) newErrors.name = nameError;

    const emailError = validateField(formData.email, emailValidator);
    if (emailError) newErrors.email = emailError;

    const passwordError = validateField(formData.password, passwordValidator);
    if (passwordError) newErrors.password = passwordError;

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: 'customer',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-bold text-primary-600 mb-2">
          CORTEX
        </h1>
        <h2 className="text-center text-2xl font-semibold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Full name"
              type="text"
              autoComplete="name"
              value={formData.name}
              onChange={handleChange('name')}
              error={errors.name}
              leftIcon={<UserIcon className="h-5 w-5" />}
              placeholder="John Doe"
            />

            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={errors.email}
              leftIcon={<EnvelopeIcon className="h-5 w-5" />}
              placeholder="you@example.com"
            />

            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange('password')}
              error={errors.password}
              leftIcon={<LockClosedIcon className="h-5 w-5" />}
              placeholder="Create a strong password"
              helperText="Must be at least 8 characters with uppercase, lowercase, and number"
            />

            <Input
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={errors.confirmPassword}
              leftIcon={<LockClosedIcon className="h-5 w-5" />}
              placeholder="Confirm your password"
            />

            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor="terms"
                className="ml-2 block text-sm text-gray-900"
              >
                I agree to the{' '}
                <a
                  href="#"
                  className="text-primary-600 hover:text-primary-500"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="#"
                  className="text-primary-600 hover:text-primary-500"
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              isLoading={isRegistering}
            >
              Create account
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
